import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  useTheme,
  IconButton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Calendar as BigCalendar,
  momentLocalizer,
  Views,
} from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import type { Notice } from "../../services/api/types";
import { mapNoticesToCalendarEventCores } from "../../utils/noticeCalendarEvents";
import CalendarDayEventsModal, {
  type CalendarDayEventSelection,
} from "../calendar/CalendarDayEventsModal";
import { lightTheme } from "../../styles/theme";
import { getNoticeEventColor } from "../../domain/calendar/eventColor";

interface DashboardCalendarEvent {
  start: Date;
  end: Date;
  title: string;
  allDay?: boolean;
  noticeId: string;
  eventKey: string;
  notice: Notice;
  fullTitle: string;
  /** When multiple events share a day, dashboard shows one badge with this count. */
  eventCount?: number;
  dayEvents?: DashboardCalendarEvent[];
}

const DotEvent: React.FC<{ event: DashboardCalendarEvent }> = ({ event }) => {
  const count = event.eventCount ?? 1;

  if (count > 1) {
    return (
      <Box
        sx={{
          minWidth: 20,
          height: 20,
          px: 0.5,
          borderRadius: 10,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          fontSize: "0.65rem",
          fontWeight: 700,
          lineHeight: 1,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "scale(1.08)",
            bgcolor: "secondary.main",
            boxShadow: (theme) =>
              `0 4px 8px ${theme.palette.mode === "dark" ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0.15)"}`,
          },
        }}
        aria-label={`${count} events`}
      >
        {count}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: "primary.main",
        cursor: "pointer",
        transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        "&:hover": {
          transform: "scale(1.4)",
          bgcolor: "secondary.main",
          boxShadow: `0 4px 8px ${lightTheme.colors.shadowBlack15}`,
        },
        "&:active": {
          transform: "scale(1.2)",
          transition: "all 0.1s ease",
        },
      }}
    />
  );
};

const localizer = momentLocalizer(moment);

const MinimalToolbar: React.FC<any> = ({ label, onNavigate, showNavigation }: any) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: showNavigation ? "space-between" : "center",
      mb: 1,
      px: showNavigation ? 0.5 : 0,
    }}
  >
    {showNavigation && (
      <IconButton
        type="button"
        size="small"
        onClick={() => onNavigate("PREV")}
        aria-label="Previous month"
        sx={{
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
    )}
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 600, color: "text.primary" }}
    >
      {label}
    </Typography>
    {showNavigation && (
      <IconButton
        type="button"
        size="small"
        onClick={() => onNavigate("NEXT")}
        aria-label="Next month"
        sx={{
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    )}
  </Box>
);

interface DashboardCalendarProps {
  notices?: Notice[];
  onSelectSlot?: (slotInfo: { start: Date; end: Date }) => void;
  showMonthNavigation?: boolean;
  selectedDate?: Date | null;
}

const DashboardCalendar: React.FC<DashboardCalendarProps> = ({
  notices = [],
  onSelectSlot,
  showMonthNavigation = false,
  selectedDate,
}) => {
  const theme = useTheme();
  const [selectedEvent, setSelectedEvent] =
    useState<DashboardCalendarEvent | null>(null);
  const [dayEventsPicker, setDayEventsPicker] = useState<
    DashboardCalendarEvent[] | null
  >(null);
  const [dayPickerDate, setDayPickerDate] = useState<Date | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  const eventColors = useMemo(() => lightTheme.colors.avatarColors, []);

  const getEventColor = useCallback(
    (noticeId: string) => getNoticeEventColor(noticeId, eventColors),
    [eventColors]
  );

  const toDayEventSelection = useCallback(
    (event: DashboardCalendarEvent): CalendarDayEventSelection => ({
      eventKey: event.eventKey,
      kind: "event",
      noticeId: event.noticeId,
      notice: event.notice,
      fullTitle: event.fullTitle,
      start: event.start,
      allDay: event.allDay,
    }),
    []
  );

  const events: DashboardCalendarEvent[] = useMemo(() => {
    const dayEvents = mapNoticesToCalendarEventCores(notices).map((event) => ({
      title: "",
      start: event.start,
      end: event.end,
      allDay: event.allDay,
      noticeId: event.noticeId,
      eventKey: event.eventKey,
      notice: event.notice,
      fullTitle: event.fullTitle,
    }));

    const byDay = new Map<string, DashboardCalendarEvent[]>();
    for (const event of dayEvents) {
      const key = moment(event.start).format("YYYY-MM-DD");
      const bucket = byDay.get(key);
      if (bucket) {
        bucket.push(event);
      } else {
        byDay.set(key, [event]);
      }
    }

    return Array.from(byDay.values()).map((eventsForDay) => {
      const sorted = [...eventsForDay].sort(
        (a, b) => a.start.getTime() - b.start.getTime()
      );
      const first = sorted[0];
      return {
        ...first,
        eventCount: sorted.length,
        dayEvents: sorted,
      };
    });
  }, [notices]);

  const handleSelectEvent = useCallback((event: DashboardCalendarEvent) => {
    if (event.dayEvents && event.dayEvents.length > 1) {
      setDayEventsPicker(event.dayEvents);
      setDayPickerDate(event.start);
      setSelectedEvent(null);
    } else {
      setSelectedEvent(event);
      setDayEventsPicker(null);
      setDayPickerDate(null);
    }
    setModalOpen(true);
  }, []);

  const handlePickDayEvent = useCallback((event: CalendarDayEventSelection) => {
    const match = dayEventsPicker?.find((e) => e.eventKey === event.eventKey);
    if (match) {
      setSelectedEvent(match);
    }
  }, [dayEventsPicker]);

  const handleBackToList = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedEvent(null);
    setDayEventsPicker(null);
    setDayPickerDate(null);
  }, []);

  const modalDayEvents = useMemo(
    () => (dayEventsPicker ?? []).map(toDayEventSelection),
    [dayEventsPicker, toDayEventSelection]
  );

  const modalSelectedEvent = useMemo(
    () => (selectedEvent ? toDayEventSelection(selectedEvent) : null),
    [selectedEvent, toDayEventSelection]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          height: 340,
          // Modern calendar styling with full theme integration
          "& .rbc-calendar": {
            fontFamily: theme.typography.fontFamily,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            touchAction: "manipulation",
          },
          // Enhanced toolbar with theme colors
          "& .rbc-toolbar": {
            backgroundColor: `${theme.palette.primary.main}08`,
            borderRadius: 1,
            mb: 1,
            p: 1.5,
            border: `1px solid ${theme.palette.divider}`,
          },
          "& .rbc-toolbar button": {
            display: showMonthNavigation ? "inline-flex" : "none",
          },
          "& .rbc-btn-group": {
            display: showMonthNavigation ? "flex" : "none",
          },
          "& .rbc-toolbar .rbc-btn-group button": {
            display: showMonthNavigation ? "inline-flex !important" : "none !important",
          },
          "& .rbc-toolbar .rbc-toolbar-label": {
            color: theme.palette.primary.main,
            fontWeight: 600,
            fontSize: "1rem",
          },
          // Modern headers with theme integration
          "& .rbc-header": {
            backgroundColor: `${theme.palette.primary.main}05`,
            color: theme.palette.text.secondary,
            fontWeight: 600,
            padding: "8px 6px",
            borderBottom: `1px solid ${theme.palette.divider}`,
            border: "none",
            borderLeft: "none !important",
            fontSize: "0.75rem",
          },
          // Clean grid-less design
          "& .rbc-month-view": { border: "none" },
          "& .rbc-month-row": { borderTop: "none !important" },
          "& .rbc-row": {
            minHeight: 22,
          },
          "& .rbc-row-segment": {
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            paddingBottom: 2,
          },
          "& .rbc-row-bg": { border: "none !important" },
          "& .rbc-day-bg": {
            border: "none !important",
          },
          "& .rbc-off-range-bg": {
            backgroundColor: theme.palette.action.disabledBackground,
            opacity: 0.3,
          },
          // Improved date cells with better interaction
          "& .rbc-date-cell": {
            padding: "6px 8px",
            minHeight: "32px",
            fontSize: "0.75rem",
          },
          "& .rbc-button-link": {
            color: theme.palette.text.secondary,
            textDecoration: "none",
            padding: "4px",
            borderRadius: 1,
            minWidth: "24px",
            minHeight: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.75rem",
          },
          // Enhanced today highlighting with better centering
          "& .rbc-today": {
            backgroundColor: `${theme.palette.primary.main}10`,
            position: "relative",
          },
          "& .rbc-today .rbc-button-link": {
            color: theme.palette.primary.main,
            fontWeight: 600,
            backgroundColor: `${theme.palette.primary.main}20`,
            borderRadius: "50%",
          },
          // Clean event styling
          "& .rbc-event": {
            backgroundColor: "transparent",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            transition: "all 0.15s ease",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          },
          "& .rbc-event-content": {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            padding: 0,
            margin: 0,
          },
          // Remove the default react-big-calendar hover pill effect
          "& .rbc-event:hover": {
            backgroundColor: "transparent !important",
            border: "none !important",
            boxShadow: "none !important",
          },
          "& .rbc-event:focus": {
            backgroundColor: "transparent !important",
            border: "none !important",
            boxShadow: "none !important",
          },
          /* Event layer sits above day backgrounds; without this, taps hit empty row-content and never select a date (esp. mobile). */
          "& .rbc-row-content": {
            pointerEvents: "none",
          },
          "& .rbc-event, & .rbc-show-more": {
            pointerEvents: "auto",
          },
          "& .rbc-show-more": {
            display: "none !important",
          },
          "@media (pointer: coarse)": {
            "& .rbc-button-link": {
              minWidth: 40,
              minHeight: 40,
              fontSize: "0.8125rem",
            },
            "& .rbc-date-cell": {
              minHeight: 40,
            },
          },
        }}
      >
        <BigCalendar
          localizer={localizer}
          events={events as any}
          date={currentDate}
          defaultView={Views.MONTH}
          views={[Views.MONTH]}
          popup
          style={{ height: "100%" }}
          onNavigate={(newDate) => setCurrentDate(newDate)}
          components={{
            toolbar: (props: any) => (
              <MinimalToolbar
                {...props}
                showNavigation={showMonthNavigation}
              />
            ),
            event: DotEvent as any,
          }}
          eventPropGetter={() => ({ style: { padding: 0, margin: 0 } })}
          dayPropGetter={(date) => {
            if (!selectedDate || !moment(date).isSame(selectedDate, "day")) {
              return {};
            }

            return {
              style: {
                backgroundColor: `${theme.palette.primary.main}18`,
                borderRadius: 8,
                boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}55`,
              },
            };
          }}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={onSelectSlot}
          selectable={onSelectSlot ? "ignoreEvents" : true}
          longPressThreshold={onSelectSlot ? 80 : 250}
        />
      </Box>

      <CalendarDayEventsModal
        open={modalOpen}
        onClose={handleCloseModal}
        dayDate={dayPickerDate ?? selectedEvent?.start ?? null}
        events={modalDayEvents}
        selectedEvent={modalSelectedEvent}
        onSelectEvent={handlePickDayEvent}
        onBackToList={handleBackToList}
        allNotices={notices}
        getEventColor={getEventColor}
      />
    </Paper>
  );
};

export default DashboardCalendar;
